import React, { useRef } from "react";
import { toast } from "react-toastify";
import { SUPPORTED_PROMPT_PLACEHOLDERS } from "./promptConstants";

/**
 * Template editor — plain text to preserve {{placeholder}} syntax exactly.
 * (Monaco not in project; React Quill would alter placeholder tokens via HTML.)
 */
export const PromptContentEditor = ({
  value,
  onChange,
  error,
  disabled = false,
}) => {
  const textareaRef = useRef(null);

  const insertAtCursor = (text) => {
    const el = textareaRef.current;
    if (!el) {
      onChange(value + text);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next = value.slice(0, start) + text + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + text.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const copyPlaceholder = async (placeholder) => {
    try {
      await navigator.clipboard.writeText(placeholder);
      toast.success(`Copied ${placeholder}`);
    } catch {
      toast.error("Unable to copy to clipboard");
    }
  };

  return (
    <div className="prompt-content-editor-wrapper">
      <div className="card border mb-2">
        <div className="card-header py-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
          <span className="fw-semibold small mb-0">Prompt Template Editor</span>
          <span className="text-muted small">
            {value?.length || 0} characters
          </span>
        </div>
        <div className="card-body p-0">
          <div className="px-3 py-2 border-bottom bg-light">
            <span className="small text-muted me-2">Quick insert:</span>
            {SUPPORTED_PROMPT_PLACEHOLDERS.map((ph) => (
              <button
                key={ph}
                type="button"
                className="btn btn-sm btn-outline-secondary me-1 mb-1"
                disabled={disabled}
                onClick={() => insertAtCursor(ph)}
              >
                {ph}
              </button>
            ))}
          </div>
          <textarea
            ref={textareaRef}
            id="promptContent"
            name="promptContent"
            rows={14}
            className={`form-control prompt-content-editor-field border-0 rounded-0 ${
              error ? "is-invalid" : ""
            }`}
            placeholder="Write your AI prompt template here. Use placeholders like {{Title}} for dynamic CMS fields."
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            spellCheck={false}
          />
        </div>
      </div>

      {error && <div className="invalid-feedback d-block mb-2">{error}</div>}

      <div className="card border bg-light">
        <div className="card-body py-3">
          <h6 className="mb-2">Supported Dynamic Placeholders</h6>
          <p className="text-muted small mb-2">
            These tokens are preserved exactly and will drive future AI popup
            field generation per page.
          </p>
          <div className="d-flex flex-wrap gap-2">
            {SUPPORTED_PROMPT_PLACEHOLDERS.map((ph) => (
              <span
                key={ph}
                className="badge bg-primary-subtle text-primary border d-inline-flex align-items-center gap-1"
                style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
              >
                {ph}
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-primary"
                  title="Copy"
                  onClick={() => copyPlaceholder(ph)}
                >
                  <i className="ri-file-copy-line"></i>
                </button>
              </span>
            ))}
          </div>
          <p className="text-muted small mt-2 mb-0">
            Example: Generate SEO content for {"{{Title}}"}, {"{{ShortDescription}}"}, {"{{FullDescription}}"}, {"{{MetaTitle}}"}, {"{{MetaKeywords}}"}, {"{{MetaDescription}}"},.
          </p>
        </div>
      </div>

      <style>{`
        .prompt-content-editor-field {
          font-family: Consolas, Monaco, "Courier New", monospace;
          font-size: 0.9rem;
          line-height: 1.5;
          min-height: 280px;
          resize: vertical;
          background-color: #fafafa;
        }
        .prompt-content-editor-field:focus {
          background-color: #fff;
          box-shadow: none;
        }
      `}</style>
    </div>
  );
};

export default PromptContentEditor;
